import Link from 'next/link';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import {
  useTrackCreditWarningPopoverClicked,
  useTrackCreditWarningPopoverDismissed,
  useTrackDepletedStateViewed,
  useTrackDepletedUpsellClicked,
} from '@air/analytics';
import { ArrowRight, CautionOutlineIcon } from '@air/next-icons';
import { AlertBanner } from '@air/primitive-alert-banner';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { NEW_TAB } from '~/constants/urls';
import { useDismissedCreditUsageAlert } from '~/hooks/creditUsageAlerts/useDismissedCreditUsageAlert';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { getCreditUsageBannerAlertCandidate } from '~/utils/creditUsageAlerts/getCreditUsageBannerAlertCandidate';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export const CreditUsageBannerAlert = memo(() => {
  const { data: subscription } = useSubscriptionContext();
  const { data: creditStats } = useCreditStats();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const [showPlansModal] = useAirModal(PlansModal);
  const { trackDepletedStateViewed } = useTrackDepletedStateViewed();
  const { trackDepletedUpsellClicked } = useTrackDepletedUpsellClicked();
  const { trackCreditWarningPopoverClicked } = useTrackCreditWarningPopoverClicked();
  const { trackCreditWarningPopoverDismissed } = useTrackCreditWarningPopoverDismissed();
  const creditAnalyticsParams = useCreditAnalyticsParams();
  const hasTrackedView = useRef(false);

  const canEditPlan = canManageSubscription(permissions);

  const alertCandidate = useMemo(() => {
    if (!creditStats || !subscription || !permissions) {
      return null;
    }

    if (!creditStats.allocated) {
      return null;
    }

    return getCreditUsageBannerAlertCandidate({
      creditStats,
      subscription,
      canEditPlan,
      showPlansModal: () => showPlansModal({ leadFormTrigger: 'credit_banner' }),
    });
  }, [canEditPlan, creditStats, permissions, showPlansModal, subscription]);

  const { isDismissed, onDismiss } = useDismissedCreditUsageAlert({
    alertType: 'banner',
    creditUsageState: alertCandidate?.creditUsageState,
    nextCycleStartAt: creditStats?.nextCycleStartAt,
    workspaceId: subscription?.workspaceId,
    persistenceType: alertCandidate?.useSessionStorage ? 'sessionStorage' : 'localStorage',
  });

  const isDepleted = alertCandidate?.creditUsageState === 'depleted';

  const handleDismiss = useCallback(() => {
    // Only track dismiss for warning states (not depleted) since there's no depleted_dismissed event
    if (!isDepleted && creditAnalyticsParams) {
      trackCreditWarningPopoverDismissed({
        type: 'banner',
        ...creditAnalyticsParams,
      });
    }
    onDismiss();
  }, [creditAnalyticsParams, isDepleted, onDismiss, trackCreditWarningPopoverDismissed]);

  // Guard prevents duplicate events; ref resets when dismissed or no longer depleted so new cycles are tracked
  useEffect(() => {
    if (alertCandidate && isDepleted && !isDismissed && creditAnalyticsParams && !hasTrackedView.current) {
      hasTrackedView.current = true;
      trackDepletedStateViewed({
        surface: 'banner',
        days_since_cycle_start: creditAnalyticsParams.days_since_cycle_start,
      });
    } else if (isDismissed || !isDepleted) {
      hasTrackedView.current = false;
    }
  }, [alertCandidate, isDepleted, creditAnalyticsParams, isDismissed, trackDepletedStateViewed]);

  const handleCtaClick = useCallback(
    (onClick?: () => void) => {
      // Only track upsell/warning clicks when user can actually upgrade (not viewing member list)
      if (canEditPlan && creditAnalyticsParams) {
        if (isDepleted) {
          trackDepletedUpsellClicked({
            surface: 'banner',
            days_since_cycle_start: creditAnalyticsParams.days_since_cycle_start,
          });
        } else {
          trackCreditWarningPopoverClicked({
            type: 'banner',
            ...creditAnalyticsParams,
          });
        }
      }
      onClick?.();
    },
    [canEditPlan, creditAnalyticsParams, isDepleted, trackCreditWarningPopoverClicked, trackDepletedUpsellClicked],
  );

  const ctaButton = useMemo(() => {
    const button = alertCandidate?.ctaButton;

    if (!button) {
      return null;
    }

    if ('href' in button) {
      return (
        <Link
          href={button.href}
          target={button.newTab ? NEW_TAB : undefined}
          rel="noopener noreferrer"
          className="font-semibold text-14 text-inherit"
          onClick={() => handleCtaClick()}
        >
          {button.text}
        </Link>
      );
    }

    return (
      <button
        className="font-semibold text-14 text-inherit"
        onClick={() => handleCtaClick(button.onClick)}
        type="button"
      >
        {button.text}
      </button>
    );
  }, [alertCandidate?.ctaButton, handleCtaClick]);

  if (!alertCandidate || isDismissed) {
    return null;
  }

  const { title, description } = alertCandidate;

  return (
    <AlertBanner
      color={isDepleted ? 'red' : 'yellow'}
      data-testid="CREDITS_DEPLETED_BANNER"
      prefix={<CautionOutlineIcon className={cn('size-4', isDepleted ? 'text-flash-11' : 'text-amber-11')} />}
      onDismiss={handleDismiss}
    >
      <span className="font-semibold">{title}</span> {description}
      {!!ctaButton && (
        <div className="ml-2 inline-flex items-center gap-1">
          {ctaButton}
          <ArrowRight className="size-4" />
        </div>
      )}
    </AlertBanner>
  );
});

CreditUsageBannerAlert.displayName = 'CreditUsageBannerAlert';
